import { useMemo, useState } from 'react'
import { Badge, Button, Field, Input, Label, StatusText, StrongText, Subheading, SurfaceCard, Switch } from '../../../components/ui'
import { ArrowPathIcon, CheckCircleIcon, PlusIcon, XCircleIcon } from '@heroicons/react/20/solid'
import { uiClasses } from '../../shared/uiClasses'
import type { FormEvent } from 'react'
import type { ActionResult, Provider } from '../types'

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint

interface ProvidersTabProps {
  onAddCustomProvider: (providerName: string) => ActionResult
  providers: Provider[]
  toggleProvider: (providerId: string) => Promise<void>
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim()
  }

  return fallback
}

export default function ProvidersTab({
  onAddCustomProvider,
  providers,
  toggleProvider,
}: ProvidersTabProps) {
  const [customProvider, setCustomProvider] = useState('')
  const [formMessage, setFormMessage] = useState<string | null>(null)
  const [formTone, setFormTone] = useState<'danger' | 'success'>('success')
  const [syncingProviderId, setSyncingProviderId] = useState<string | null>(null)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)

  const normalizedProviderName = customProvider.trim()
  const isSubmitDisabled = normalizedProviderName.length === 0
  const duplicateProvider = useMemo(
    () =>
      providers.some((provider) => provider.name.trim().toLowerCase() === normalizedProviderName.toLowerCase()),
    [normalizedProviderName, providers]
  )
  const connectedCount = providers.filter((provider) => provider.connected).length
  const disconnectedCount = providers.length - connectedCount
  const connectedProviders = providers.filter((provider) => provider.connected)
  const disconnectedProviders = providers.filter((provider) => !provider.connected)
  const connectionRate = providers.length > 0 ? Math.round((connectedCount / providers.length) * 100) : 0

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFormMessage(null)

    const result = onAddCustomProvider(normalizedProviderName)
    if (!result.ok) {
      setFormTone('danger')
      setFormMessage(
        'error' in result
          ? result.error
          : '[PROVIDER_CREATE_FAILED] Failed to add provider.'
      )
      return
    }

    setCustomProvider('')
    setFormTone('success')
    setFormMessage(`Added provider source "${normalizedProviderName}".`)
  }

  async function handleToggle(providerId: string) {
    setSyncMessage(null)
    setSyncingProviderId(providerId)

    try {
      await toggleProvider(providerId)
    } catch (error) {
      setSyncMessage(errorMessage(error, '[PROVIDER_SYNC_FAILED] Failed to sync provider status.'))
    } finally {
      setSyncingProviderId(null)
    }
  }

  return (
    <div className="space-y-6">
      <section className="providers-metrics-grid" aria-label="Provider connection summary">
        <article className="providers-metric-card">
          <p className={`providers-metric-value providers-metric-value-emphasis ${TEXT_PRIMARY}`}>{connectedCount}</p>
          <p className={`providers-metric-label ${TEXT_SUBTLE}`}>Connected</p>
        </article>
        <article className="providers-metric-card">
          <p className={`providers-metric-value ${TEXT_PRIMARY}`}>{disconnectedCount}</p>
          <p className={`providers-metric-label ${TEXT_SUBTLE}`}>Disconnected</p>
        </article>
        <article className="providers-metric-card">
          <p className={`providers-metric-value ${TEXT_PRIMARY}`}>{providers.length}</p>
          <p className={`providers-metric-label ${TEXT_SUBTLE}`}>Total sources</p>
        </article>
        <article className="providers-metric-card">
          <p className={`providers-metric-value providers-metric-value-emphasis ${TEXT_PRIMARY}`}>{connectionRate}%</p>
          <p className={`providers-metric-label ${TEXT_SUBTLE}`}>Connection rate</p>
        </article>
      </section>

      <SurfaceCard as="article" className="providers-composer-shell">
        <div className="flex items-center justify-between gap-3">
          <Subheading variant="dashboard">Add provider source</Subheading>
          <Badge variant="zinc">
            {connectedCount}/{providers.length} active
          </Badge>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 providers-add-form">
          <Field>
            <Label htmlFor="custom-provider-name">Provider name</Label>
            <Input
              id="custom-provider-name"
              value={customProvider}
              onChange={(event) => {
                setCustomProvider(event.target.value)
                setFormMessage(null)
              }}
              placeholder="Provider name"
              required
            />
          </Field>
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={isSubmitDisabled || duplicateProvider}>
              <PlusIcon data-slot="icon" />
              Add source
            </Button>
            <p className={`text-xs/5 ${TEXT_FAINT}`}>Custom providers are stored locally for your account.</p>
          </div>
          {duplicateProvider && normalizedProviderName && (
            <StatusText size="xs" tone="danger">A provider with this name already exists.</StatusText>
          )}
          {formMessage && (
            <StatusText tone={formTone === 'danger' ? 'danger' : 'success'} aria-live="polite">
              {formMessage}
            </StatusText>
          )}
          <div className="providers-connection-meter">
            <div className="providers-connection-meter-track" aria-hidden="true">
              <span className="providers-connection-meter-fill" style={{ width: `${connectionRate}%` }} />
            </div>
            <p className={`text-xs/5 ${TEXT_FAINT}`}>{connectionRate}% of your providers are currently connected.</p>
          </div>
        </form>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex items-center justify-between gap-3">
          <Subheading variant="dashboard">Provider connections</Subheading>
          <p className={`text-xs/5 ${TEXT_FAINT}`}>Toggle connection state and refresh sync status per source.</p>
        </div>

        <div className="providers-status-grid mt-4">
          <section className="providers-status-panel">
            <div className="flex items-center justify-between gap-2">
              <StrongText className="inline-flex items-center gap-1.5" tone="secondary">
                <CheckCircleIcon className="size-4 text-emerald-600" />
                Connected
              </StrongText>
              <Badge variant="green">
                {connectedProviders.length}
              </Badge>
            </div>
            <div className="providers-source-list mt-3">
              {connectedProviders.length > 0 ? (
                connectedProviders.map((provider) => {
                  const syncing = syncingProviderId === provider.id
                  return (
                    <article key={provider.id} className="providers-source-item">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <StrongText className="truncate">{provider.name}</StrongText>
                          <p className={`text-xs/5 ${TEXT_SUBTLE}`}>{provider.type}</p>
                        </div>
                        <span className="providers-state-dot providers-state-dot-connected">Connected</span>
                      </div>
                      <p className={`mt-1.5 text-xs/5 ${TEXT_SUBTLE}`}>Last sync {provider.lastSync}</p>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        {syncing ? (
                          <p className={`inline-flex items-center gap-1 text-xs/5 ${TEXT_SUBTLE}`} aria-live="polite">
                            <ArrowPathIcon className="size-3.5 animate-spin" />
                            Syncing...
                          </p>
                        ) : (
                          <span className={`text-xs/5 ${TEXT_FAINT}`}>Auto-sync enabled</span>
                        )}
                        <Switch
                          checked={provider.connected}
                          onCheckedChange={() => void handleToggle(provider.id)}
                          variant="accent"
                          disabled={syncing}
                        />
                      </div>
                    </article>
                  )
                })
              ) : (
                <p className={`text-sm/6 ${TEXT_SUBTLE}`}>No connected providers yet.</p>
              )}
            </div>
          </section>

          <section className="providers-status-panel">
            <div className="flex items-center justify-between gap-2">
              <StrongText className="inline-flex items-center gap-1.5" tone="secondary">
                <XCircleIcon className="size-4 text-zinc-500" />
                Disconnected
              </StrongText>
              <Badge variant="zinc">
                {disconnectedProviders.length}
              </Badge>
            </div>
            <div className="providers-source-list mt-3">
              {disconnectedProviders.length > 0 ? (
                disconnectedProviders.map((provider) => {
                  const syncing = syncingProviderId === provider.id
                  return (
                    <article key={provider.id} className="providers-source-item">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <StrongText className="truncate">{provider.name}</StrongText>
                          <p className={`text-xs/5 ${TEXT_SUBTLE}`}>{provider.type}</p>
                        </div>
                        <span className="providers-state-dot providers-state-dot-disconnected">Disconnected</span>
                      </div>
                      <p className={`mt-1.5 text-xs/5 ${TEXT_SUBTLE}`}>Last sync {provider.lastSync}</p>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        {syncing ? (
                          <p className={`inline-flex items-center gap-1 text-xs/5 ${TEXT_SUBTLE}`} aria-live="polite">
                            <ArrowPathIcon className="size-3.5 animate-spin" />
                            Syncing...
                          </p>
                        ) : (
                          <span className={`text-xs/5 ${TEXT_FAINT}`}>Sync paused</span>
                        )}
                        <Switch
                          checked={provider.connected}
                          onCheckedChange={() => void handleToggle(provider.id)}
                          variant="accent"
                          disabled={syncing}
                        />
                      </div>
                    </article>
                  )
                })
              ) : (
                <p className={`text-sm/6 ${TEXT_SUBTLE}`}>No disconnected providers.</p>
              )}
            </div>
          </section>
        </div>
        {syncMessage && <StatusText className="mt-3" tone="danger" aria-live="polite">{syncMessage}</StatusText>}
      </SurfaceCard>
    </div>
  )
}
